import React from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { Button, FlexContainer, MenuText, Modal, UtilityAlertIcon } from '@placer-ui/components';
import { SupportRequestModalContent } from 'features/my-zone/support-requests/components/support-requests-modals/components/location-modal-content/support-request-modal-content';
import { TitleModalHeader } from 'features/my-zone/support-requests/components/support-requests-modals/components/title-modal-header/title-modal-header';
import { useFormsSectionManager } from 'features/my-zone/support-requests/components/support-requests-modals/modals/request-new-location/request-property/hooks/use-forms-section-manager/use-forms-section-manager';
import type { PropertyFormValues } from 'features/my-zone/support-requests/components/support-requests-modals/modals/request-new-location/request-property/types';
import type { SupportInputType } from 'features/my-zone/support-requests/components/support-requests-modals/types';
import { ControlledToggleButton } from 'features/my-zone/support-requests/components/support-requests-modals/components/inputs/controlled-toggle-button';
import { SupportEmailInput } from 'features/my-zone/support-requests/components/support-requests-modals/components/inputs/controlled-support-email-input/support-email-input';
import styles from './request-property.module.scss';
import { useOpenSupportRequest } from 'features/my-zone/support-requests/components/support-requests-modals/support-modal-manager';
import { getFeatureFlags } from 'core/flow-control';
import { useResetSupportState } from 'features/my-zone/support-requests/hooks/use-reset-support-state';

type PropertyRequestOption = {
    value: SupportInputType;
    label: string;
};

const propertyTypeOptions: PropertyRequestOption[] = [
    { value: 'manual', label: 'Enter Property Details' },
    { value: 'file', label: 'Upload File' },
];

const RequestPropertyInner = () => {
    const formController = useFormContext<PropertyFormValues>();
    const { closeSupportRequest, openSupportRequest } = useOpenSupportRequest();
    const { resetState } = useResetSupportState();
    const { enable_request_properties_sales_flow_ff } = getFeatureFlags();

    const formsSectionManager = useFormsSectionManager(formController);
    const handleBack = async () => {
        if (!formsSectionManager.activeSectionIndex) {
            openSupportRequest({ flowType: 'typeSelection' });
            closeSupportRequest('property');
            return;
        }

        await formsSectionManager.sectionConfig.prevButton?.onClick?.({
            onNextHandler: handleBack,
        });

        if (!formsSectionManager.sectionConfig.prevButton?.preventDefault) {
            formsSectionManager.previousSection();
        }
    };

    const sectionConfig = formsSectionManager.sectionConfig ?? {};
    const FormSection = formsSectionManager.FormSection;

    const handleNextButtonClicked = async () => {
        try {
            const nextLogic = formsSectionManager.isLastStep
                ? handleFormSubmit
                : formsSectionManager.nextSection;

            const stop = await sectionConfig.nextButton?.onClick?.({
                onNextHandler: nextLogic,
                sectionManager: formsSectionManager,
                formControl: formController,
            });

            if (!stop) {
                await nextLogic();
            }
        } catch (e) {
            console.log(e);
        }
    };

    const handleFormSubmit = async () => {
        try {
            formController.reset();
            resetState();
            closeSupportRequest('property');
        } catch (e) {
            console.log(e);
        }
    };

    const sheetsCount = formController.getValues('parsedData')?.sheetsCount || 0;
    const activeSectionIsMapping = formsSectionManager.activeSectionIndex === 1;
    const shouldShowExcelWarning = activeSectionIsMapping && sheetsCount > 1; // TODO - add stepType to config

    const nextButtonText =
        sectionConfig.nextButton?.text ?? (formsSectionManager.isLastStep ? 'Submit' : 'Next');
    const prevButtonText = sectionConfig.prevButton?.text ?? 'Back';
    const isDisplayPrevButton = !sectionConfig.prevButton?.isHidden;
    const isPrevButtonDisabled = !!sectionConfig.prevButton?.isDisabled;
    const isNextButtonLoading =
        !!sectionConfig.nextButton?.isLoading || formController.formState.isSubmitting;
    const isDisplayNextButton = !sectionConfig.nextButton?.isHidden;
    const isNextDisabled =
        !formController.formState.isValid ||
        formController.formState.isSubmitting ||
        !!sectionConfig.nextButton?.isDisabled;

    return (
        <>
            {sectionConfig.title && sectionConfig.subTitle && (
                <TitleModalHeader
                    title={sectionConfig.title}
                    subTitle={sectionConfig.subTitle}
                    elevioId={sectionConfig?.elevioId}
                />
            )}
            <SupportRequestModalContent
                testId="request-property"
                fullSize={['data-validation'].includes(sectionConfig.type)}
                avoidPadding={['request-properties'].includes(sectionConfig.type)}
                requestModalType="property"
            >
                <div className={styles.propertyFormContainer} data-testid={sectionConfig.testId}>
                    {formsSectionManager.activeSectionIndex === 0 &&
                        !enable_request_properties_sales_flow_ff && (
                            <FlexContainer gap={16} flexDirection="column" margin="0 0 16px 0">
                                <ControlledToggleButton
                                    control={formController.control}
                                    name="inputType"
                                    testId="request-property-toggle"
                                    size="default"
                                    fullWidth
                                    items={propertyTypeOptions}
                                    label="Select one way to provide info:"
                                />
                                <SupportEmailInput />
                            </FlexContainer>
                        )}
                    {FormSection && (
                        <FormSection
                            sectionManager={formsSectionManager}
                            onNextHandler={handleNextButtonClicked}
                            {...sectionConfig.formData}
                        />
                    )}
                </div>
            </SupportRequestModalContent>
            <Modal.Footer>
                <Modal.Footer.RightControls>
                    {isDisplayPrevButton && (
                        <Button
                            testId={sectionConfig.prevButton?.testId ?? 'request-property-back'}
                            variant="secondary-blue"
                            fullWidth
                            disabled={isPrevButtonDisabled}
                            onClick={handleBack}
                        >
                            {prevButtonText}
                        </Button>
                    )}
                    {isDisplayNextButton && (
                        <Button
                            tooltip={{
                                title: !formController.formState.isValid
                                    ? 'Please fill all mandatory fields to proceed.'
                                    : '',
                                tooltipPositioning: {
                                    appendToParent: true,
                                },
                            }}
                            testId={sectionConfig.nextButton?.testId ?? 'request-property-next'}
                            variant="primary-brand"
                            isLoading={isNextButtonLoading}
                            disabled={isNextDisabled}
                            fullWidth
                            onClick={handleNextButtonClicked}
                        >
                            {nextButtonText}
                        </Button>
                    )}
                </Modal.Footer.RightControls>
                {shouldShowExcelWarning && (
                    <Modal.Footer.LeftControls>
                        <FlexContainer gap={8} alignItems="center">
                            <UtilityAlertIcon />
                            <MenuText
                                variant="semi-bold"
                                color="secondary"
                                testId="request-property-first-sheet-message"
                            >
                                Only the first sheet in the Excel file you uploaded will be
                                processed.
                            </MenuText>
                        </FlexContainer>
                    </Modal.Footer.LeftControls>
                )}
            </Modal.Footer>
        </>
    );
};

export const RequestProperty = () => {
    const formController = useForm<PropertyFormValues>({
        defaultValues: {
            inputType: 'manual',
            properties: [
                {
                    name: '',
                    address: '',
                    zip_code: '',
                },
            ],
            additionalInformation: '',
        },
        mode: 'all',
    });

    return (
        <FormProvider {...formController}>
            <RequestPropertyInner />
        </FormProvider>
    );
};
